// @boty/ai - OpenAI Provider abstraction
// Provides shared OpenAI client initialization and model constants

import OpenAI from 'openai';

/**
 * Error codes for AI provider failures
 */
export type AIProviderErrorCode =
  | 'MISSING_API_KEY'
  | 'API_ERROR'
  | 'RATE_LIMIT_EXCEEDED'
  | 'QUOTA_EXCEEDED'
  | 'INVALID_REQUEST'
  | 'TIMEOUT'
  | 'PARSE_ERROR';

/**
 * Custom error class for AI provider failures
 */
export class AIProviderError extends Error {
  code: AIProviderErrorCode;

  constructor(code: AIProviderErrorCode, message: string) {
    super(message);
    this.name = 'AIProviderError';
    this.code = code;
  }
}

/**
 * Available AI models with their use cases
 */
export const AI_MODELS = {
  /** GPT-4o-mini: Cost-efficient text model for content/name/question generation ($0.15/1M input, $0.60/1M output) */
  GPT4O_MINI: 'gpt-4o-mini',
  /** GPT-5 mini: Stronger instruction-following + multilingual quality ($0.25/1M input, $2.00/1M output) */
  GPT5_MINI: 'gpt-5-mini',
  /** GPT-5.4 mini: Modern reasoning model used for consumer chat with reasoning_effort=minimal ($0.75/1M input, $4.50/1M output) */
  GPT5_4_MINI: 'gpt-5.4-mini',
  /** GPT-4o: Vision and complex tasks ($5/1M input, $15/1M output) */
  GPT4O: 'gpt-4o',
} as const;

export type AIModel = (typeof AI_MODELS)[keyof typeof AI_MODELS];

// Singleton OpenAI client instance
let openaiClient: OpenAI | null = null;

/**
 * Gets the shared OpenAI client instance
 * Creates the client on first call, reuses on subsequent calls
 *
 * @returns OpenAI client instance
 * @throws AIProviderError if OPENAI_API_KEY is not set
 *
 * @example
 * ```typescript
 * const client = getOpenAIClient();
 * const response = await client.chat.completions.create({
 *   model: AI_MODELS.GPT4O_MINI,
 *   messages: [{ role: 'user', content: 'Hello' }],
 * });
 * ```
 */
export function getOpenAIClient(): OpenAI {
  if (!openaiClient) {
    const apiKey = process.env.OPENAI_API_KEY;

    if (!apiKey) {
      throw new AIProviderError(
        'MISSING_API_KEY',
        'OPENAI_API_KEY environment variable is required'
      );
    }

    openaiClient = new OpenAI({ apiKey });
  }

  return openaiClient;
}

/**
 * Resets the OpenAI client (useful for testing)
 */
export function resetOpenAIClient(): void {
  openaiClient = null;
}

/**
 * Delays execution for retry with exponential backoff
 * @param attempt - Current retry attempt (0-indexed)
 * @param baseDelay - Base delay in milliseconds (default: 1000)
 * @param maxDelay - Maximum delay in milliseconds (default: 10000)
 */
export async function retryDelay(
  attempt: number,
  baseDelay: number = 1000,
  maxDelay: number = 10000
): Promise<void> {
  const delayTime = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);
  return new Promise((resolve) => setTimeout(resolve, delayTime));
}

/**
 * Handles OpenAI API errors and converts them to AIProviderError
 * @param error - The caught error
 * @param context - Optional context for error message
 */
export function handleOpenAIError(error: unknown, context?: string): never {
  const prefix = context ? `[${context}] ` : '';

  if (error instanceof OpenAI.APIError) {
    if (error.status === 429) {
      // OpenAI uses 429 for both rate limits and exhausted account credits.
      // Quota exhaustion must not surface as "rate limit" — the UI tells users
      // to slow down, masking a billing outage.
      if (error.code === 'insufficient_quota') {
        throw new AIProviderError(
          'QUOTA_EXCEEDED',
          `${prefix}AI service is temporarily unavailable. Please try again later.`
        );
      }
      throw new AIProviderError(
        'RATE_LIMIT_EXCEEDED',
        `${prefix}Rate limit exceeded. Please try again later.`
      );
    }

    if (error.status === 400) {
      throw new AIProviderError(
        'INVALID_REQUEST',
        `${prefix}Invalid request: ${error.message}`
      );
    }

    if (error.code === 'ETIMEDOUT' || error.message?.includes('timeout')) {
      throw new AIProviderError(
        'TIMEOUT',
        `${prefix}Request timed out. Please try again.`
      );
    }

    throw new AIProviderError(
      'API_ERROR',
      `${prefix}${error.message || 'OpenAI API error'}`
    );
  }

  if (error instanceof Error) {
    throw new AIProviderError('API_ERROR', `${prefix}${error.message}`);
  }

  throw new AIProviderError('API_ERROR', `${prefix}Unknown error occurred`);
}
